type Listener = (channel: string, payload: unknown) => void;

interface Envelope {
  channel?: string;
  data?: unknown;
}

const WS_BASE = import.meta.env.VITE_WS_BASE ?? "ws://localhost:4000";

/**
 * Singleton WS client wired to the gateway `/ws` bridge.
 *
 * Server wraps every message as `{ channel, data }`; this client unwraps and
 * dispatches to per-channel listeners. Auto-reconnects with exponential
 * backoff and re-sends every active subscription on reconnect.
 */
class MarketSocket {
  private ws: WebSocket | null = null;
  private listeners = new Map<string, Set<Listener>>();
  private subscribed = new Set<string>();
  private backoff = 1_000;
  private intentionalClose = false;

  connect(): void {
    this.intentionalClose = false;
    this.open();
  }

  subscribe(channel: string, fn: Listener): () => void {
    let set = this.listeners.get(channel);
    if (!set) {
      set = new Set();
      this.listeners.set(channel, set);
    }
    set.add(fn);
    if (!this.subscribed.has(channel)) {
      this.subscribed.add(channel);
      this.send({ op: "subscribe", channels: [channel] });
    }
    return () => {
      set?.delete(fn);
      if (set && set.size === 0) {
        this.listeners.delete(channel);
        this.subscribed.delete(channel);
        this.send({ op: "unsubscribe", channels: [channel] });
      }
    };
  }

  private send(payload: unknown): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify(payload));
    } catch {
      // socket about to close; reconnect handler will resubscribe
    }
  }

  private open(): void {
    const url = `${WS_BASE.replace(/^http/, "ws")}/ws`;
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.addEventListener("open", () => {
      this.backoff = 1_000;
      if (this.subscribed.size > 0) {
        ws.send(
          JSON.stringify({
            op: "subscribe",
            channels: [...this.subscribed],
          }),
        );
      }
    });

    ws.addEventListener("message", (ev) => {
      let parsed: Envelope | null = null;
      try {
        parsed = JSON.parse(ev.data as string);
      } catch {
        return;
      }
      if (!parsed || typeof parsed !== "object" || !parsed.channel) return;
      const set = this.listeners.get(parsed.channel);
      if (!set) return;
      for (const fn of set) fn(parsed.channel, parsed.data);
    });

    ws.addEventListener("close", () => {
      this.ws = null;
      if (this.intentionalClose) return;
      const wait = this.backoff + Math.floor(Math.random() * 250);
      this.backoff = Math.min(this.backoff * 2, 30_000);
      setTimeout(() => this.open(), wait);
    });

    ws.addEventListener("error", () => {
      ws.close();
    });
  }
}

export const marketSocket = new MarketSocket();
marketSocket.connect();
